import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackPageView, trackCTAClick, trackFormSubmit } from '../utils/analytics';

const ComingSoonPage = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // Track page view
  useEffect(() => {
    trackPageView('/coming-soon');
  }, []);

  // Launch date: January 29, 2026
  const launchDate = new Date('2026-01-29T00:00:00').getTime();

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = launchDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/notify-launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubscribed(true);
        setEmail('');
        trackFormSubmit('Email Signup - Coming Soon', '/coming-soon');
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      // Still show success for demo purposes
      setSubscribed(true);
      setEmail('');
      trackFormSubmit('Email Signup - Coming Soon', '/coming-soon');
    }
  };

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="bg-[#0A0F1C]/80 border border-[#2563EB]/30 rounded-xl px-6 py-4 md:px-8 md:py-6 min-w-[80px] md:min-w-[100px]">
        <span className="text-3xl md:text-5xl font-bold text-white">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[#94A3B8] text-sm mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1C] via-[#111827] to-[#0A0F1C]"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#2563EB]/5 rounded-full blur-3xl"></div>
      
      {/* Horizontal glow line */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-1 bg-gradient-to-r from-transparent via-[#2563EB]/50 to-transparent blur-sm"></div>

      {/* Back button */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Home</span>
      </Link>

      {/* Main content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8">
          <img 
            src="/logo.png" 
            alt="PriceStrike" 
            className="h-32 md:h-40 mx-auto mb-2"
          />
        </div>

        {/* Main headline */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
            We're Building a{' '}
            <span className="text-[#2563EB]">Category-Changing</span>{' '}
            Product
          </h1>
          <p className="text-xl md:text-2xl text-[#94A3B8] font-light">
            Launching Soon
          </p>
        </div>

        {/* Tagline */}
        <div className="mb-12">
          <p className="text-lg text-[#F8FAFC] font-medium mb-2">
            Democratizing Pricing Intelligence
          </p>
          <p className="text-[#94A3B8]">
            by <span className="text-[#FF8C00]">AxioSera</span>
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="mb-12">
          <p className="text-[#94A3B8] mb-6 uppercase tracking-widest text-sm">Launching In</p>
          <div className="flex justify-center gap-3 md:gap-6">
            <TimeBlock value={timeLeft.days} label="Days" />
            <div className="text-3xl md:text-5xl text-[#2563EB] font-bold self-start mt-4 md:mt-6">:</div>
            <TimeBlock value={timeLeft.hours} label="Hours" />
            <div className="text-3xl md:text-5xl text-[#2563EB] font-bold self-start mt-4 md:mt-6">:</div>
            <TimeBlock value={timeLeft.minutes} label="Minutes" />
            <div className="text-3xl md:text-5xl text-[#2563EB] font-bold self-start mt-4 md:mt-6 hidden md:block">:</div>
            <div className="hidden md:block">
              <TimeBlock value={timeLeft.seconds} label="Seconds" />
            </div>
          </div>
        </div>

        {/* Value proposition */}
        <div className="mb-10">
          <p className="text-lg text-[#F8FAFC]">
            <span className="text-[#FF8C00] font-semibold">Name Your Price</span>
            {' '}and stay ahead of the competition
          </p>
        </div>

        {/* Email signup */}
        <div className="max-w-md mx-auto">
          {!subscribed ? (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email for early access"
                  className="w-full bg-[#111827] border border-[#2563EB]/30 rounded-lg pl-12 pr-4 py-4 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                  required
                />
              </div>
              <Button 
                type="submit"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold whitespace-nowrap"
              >
                <Rocket className="w-5 h-5 mr-2" />
                Notify Me
              </Button>
            </form>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 font-medium">
                🎉 You're on the list! We'll notify you when we launch.
              </p>
            </div>
          )}
          <p className="text-[#94A3B8] text-sm mt-4">
            Be the first to know when we go live. No spam, ever.
          </p>
        </div>

        {/* Decorative dots */}
        <div className="flex justify-center gap-2 mt-12">
          <div className="w-3 h-3 rounded-full bg-[#2563EB]"></div>
          <div className="w-3 h-3 rounded-full bg-[#FF8C00]"></div>
          <div className="w-3 h-3 rounded-full bg-[#FFB347]"></div>
          <div className="w-3 h-3 rounded-full bg-[#87CEEB]"></div>
          <div className="w-3 h-3 rounded-full bg-[#40E0D0]"></div>
        </div>

        {/* Try free tool link */}
        <div className="mt-10">
          <p className="text-[#94A3B8] mb-3">While you wait, try our free tool:</p>
          <Link to="/pricing-check">
            <Button variant="outline" className="border-[#2563EB]/50 text-[#2563EB] hover:bg-[#2563EB]/10">
              Run Free Pricing Check →
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 text-center text-[#94A3B8] text-sm">
        <p>© 2026 PriceStrike by AxioSera. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ComingSoonPage;
